import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRoomStore } from '../../../stores/roomStore';
import { Button } from '../../common/Button';
import type { PersistentRoom } from '../../../types/room';

interface RoomDangerZoneProps {
  room: PersistentRoom;
}

/**
 * Danger zone with delete room option.
 */
export function RoomDangerZone({ room }: RoomDangerZoneProps) {
  const navigate = useNavigate();
  const { deleteRoom } = useRoomStore();
  const [isDeleting, setIsDeleting] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await deleteRoom(room.id);
      navigate('/');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <section className="card p-6 border-danger/30">
      <h2 className="text-lg font-semibold text-danger mb-4">Danger Zone</h2>

      {!showConfirm ? (
        <div className="flex items-center justify-between">
          <div>
            <p className="font-medium">Delete this room</p>
            <p className="text-sm text-gray-400">
              Permanently delete this room and all its data
            </p>
          </div>
          <Button variant="danger" onClick={() => setShowConfirm(true)}>
            Delete Room
          </Button>
        </div>
      ) : (
        <div className="bg-danger/10 border border-danger/30 rounded-lg p-4">
          <p className="text-sm mb-4">
            Are you sure you want to delete <strong>{room.name}</strong>? This cannot be undone.
          </p>
          <div className="flex gap-3">
            <Button
              variant="danger"
              onClick={handleDelete}
              isLoading={isDeleting}
            >
              Yes, Delete
            </Button>
            <Button
              variant="secondary"
              onClick={() => setShowConfirm(false)}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}
